import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

// import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as RolesIcon } from '../../assets/icons/sidebar/role.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { RequestRoleInterFace, RoleInterFace } from '../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import {
  fetchAllRoles,
  setRoleFilterData,
  updateRoleFilters,
  fetchListFilters,
  setSortData,
} from '../../redux/slice/RoleManagementSlice';
import { RootState } from '../../redux/store';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
// import PageTitle from '../../shared-components/page-title/PageTitle';
import RoleSidePanel from '../../shared-components/table-side-panel/RoleSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
// import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import roleApi from '../../utils/api/roles';

/**
 * Role management function component
 * @returns {React.ReactElement} - The role management page
 */
function RoleManagement() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('userManagement');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const {
    roles,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.roles);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Handle the fetch data for Api call function
   * @param data - The request data
   */
  const fetchData = (data: RequestRoleInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllRoles({
          ...data,
        }),
      );
    }
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.set('roleId', 'add-role');
    setSearchParameters(parameters);
  };
  /**
   * Handle the Edit Role function
   */
  // const handleEditRole = (currentData: RoleInterFace) => {
  //   if (currentData._id) {
  //     searchParameters.set('roleId', currentData._id);
  //     setSearchParameters(searchParameters);
  //   }
  // };

  /**
   * Handle the sort field function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setRoleFilterData(sortData));
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle page change
   * @param pageNumber - The new page number
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the open side panel on row click function
   * @param _ - Unused parameter
   * @param row - The facility tag data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: RoleInterFace) => {
    if (!row?._id) return;
    // Set or update the Id param
    searchParameters.set('roleId', row._id);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof RoleInterFace,
      sortAllow: true,
    },
    {
      label: t('roleName'),
      accessorKey: 'name' as keyof RoleInterFace,
      sortAllow: true,
      queryParamsKey: 'roleId',
      sidePanelComponent: <RoleSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('responsibility'),
      accessorKey: 'responsibility' as keyof RoleInterFace,
      sortAllow: false,
    },
    // {
    //   label: t('actions'),
    //   accessorKey: '' as keyof RoleInterFace,
    //   columnWidth: 30,
    //   /**
    //    * Action button in table
    //    * @param station - The station data (RoleInterFace)
    //    * @returns {React.ReactElement} - The action buttons
    //    */
    //   // eslint-disable-next-line react/no-unstable-nested-components
    //   customData: (station: RoleInterFace) => (
    //     <div
    //       className="action-button-group"
    //       onClick={() => handleEditRole(station)}
    //       role="presentation">
    //       <div className="action-button-item view-color">
    //         <ActionEditIcon />
    //       </div>
    //     </div>
    //   ),
    // },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('roleName'), accessorKey: 'name' },
    { label: t('responsibility'), accessorKey: 'responsibility' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    roleApi
      .fetchRolesExport(removeEmptyKeys({ ...queryParameters, sortBy, sortField }))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: RoleInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'date':
                return ConvertUTCtoDate(row?.createdAt || 'N/A');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'roles-list.csv');
          } else {
            exportPDF(headers, rows, 'roles-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('roleId');
    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('roles'),
          icon: <RolesIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('roles'),
          icon: <RolesIcon />,
          count: totalResults,
        }),
      );
    }
  }, [searchParameters, totalResults]);

  return (
    <div className="role-management-content">
      <div>
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          header={column}
          data={roles}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateRoleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="roleId"
          rowClickSidePanel={<RoleSidePanel fetchIdFrom="queryParams" />}
          leftTopContent={
            <MyButton
              label={t('addRole')}
              onClickFunc={submit}
              buttonType="submit"
              svgIcon={<PlusIcon />}
            />
          }
          tableHeight="calc(100vh - 220px)"
        />
      </div>
    </div>
  );
}

export default RoleManagement;
